import { useEffect, useState } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell } from "recharts";
import { Users, Eye, Smartphone, Monitor, Globe2, Link2, MapPin, Languages, TrendingUp } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const PAGE_LABELS = {
  "/": "Accueil",
  "/services": "Services",
  "/secteurs": "Secteurs",
  "/produits": "Catalogue",
  "/fret": "Fret",
  "/suivi": "Suivi colis",
  "/a-propos": "À propos",
  "/contact": "Contact",
  "/login": "Connexion",
  "/register": "Inscription",
};
const REF_LABELS = {
  direct: "Direct / Marque-page",
  google: "Google",
  facebook: "Facebook",
  instagram: "Instagram",
  tiktok: "TikTok",
  whatsapp: "WhatsApp",
  linkedin: "LinkedIn",
  twitter: "X / Twitter",
  youtube: "YouTube",
  bing: "Bing",
};

function StatCard({ icon: Icon, label, value, hint }) {
  return (
    <div className="card-flat p-6">
      <Icon className="text-[var(--accent)]" size={20} />
      <p className="text-xs uppercase tracking-widest text-[var(--ink-soft)] mt-4">{label}</p>
      <p className="font-display text-3xl font-black mt-1">{value ?? "—"}</p>
      {hint && <p className="text-[10px] text-[var(--ink-soft)] uppercase tracking-widest mt-1">{hint}</p>}
    </div>
  );
}

function List({ title, items, icon: Icon, mapLabel = (k) => k, unit = "vues" }) {
  const max = Math.max(1, ...items.map((x) => x.count));
  return (
    <div className="card-flat p-6">
      <p className="kicker flex items-center gap-1"><Icon size={12} /> {title}</p>
      {items.length === 0 ? (
        <p className="text-sm text-[var(--ink-soft)] mt-4">Aucune donnée sur la période.</p>
      ) : (
        <ul className="mt-4 space-y-2">
          {items.map((row) => (
            <li key={row.key} className="text-sm">
              <div className="flex justify-between items-center gap-3">
                <span className="truncate flex-1" title={row.key}>{mapLabel(row.key)}</span>
                <span className="tabular-nums text-[var(--ink-soft)]">{row.count} <span className="text-[10px] uppercase">{unit}</span></span>
              </div>
              <div className="h-1.5 mt-1 bg-[var(--bg-soft)] rounded overflow-hidden">
                <div className="h-full bg-[var(--accent)]" style={{ width: `${(row.count / max) * 100}%` }} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function AnalyticsPanel() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(7);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let stop = false;
    setLoading(true);
    api.get(`/admin/analytics/summary?days=${days}`)
      .then((r) => { if (!stop) setData(r.data); })
      .catch((e) => { if (!stop) setErr(formatApiError(e)); })
      .finally(() => { if (!stop) setLoading(false); });
    return () => { stop = true; };
  }, [days]);

  if (loading && !data) return <p className="text-sm text-[var(--ink-soft)]">Chargement des statistiques…</p>;
  if (err) return <p className="text-sm text-red-500">{err}</p>;
  if (!data) return null;

  const chartData = data.daily.map((d) => ({
    day: d.day.slice(5), // MM-DD
    visitors: d.visitors,
    pageviews: d.pageviews,
  }));

  const totalDevices = (data.devices.mobile || 0) + (data.devices.desktop || 0);
  const devicePie = totalDevices > 0
    ? [
        { name: "Mobile", value: data.devices.mobile, color: "#FF3B30" },
        { name: "Desktop", value: data.devices.desktop, color: "#003366" },
      ]
    : [];
  const mobilePct = totalDevices > 0 ? Math.round((data.devices.mobile / totalDevices) * 100) : 0;

  return (
    <div className="space-y-6" data-testid="admin-analytics">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="kicker !text-[var(--secondary)] flex items-center gap-1"><TrendingUp size={12} /> Trafic du site</p>
          <p className="text-sm text-[var(--ink-soft)] mt-1">
            Période : {data.range.since} → {data.range.until} ({data.range.days} jours)
          </p>
        </div>
        <div className="flex gap-1 bg-[var(--bg-soft)] rounded-full p-1">
          {[7, 14, 30].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              data-testid={`analytics-range-${d}`}
              className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest transition-colors ${days === d ? "bg-[var(--ink)] text-white" : "text-[var(--ink-soft)] hover:text-[var(--ink)]"}`}
            >
              {d} jours
            </button>
          ))}
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Visiteurs aujourd'hui" value={data.today.visitors} hint={`${data.today.pageviews} pages vues`} />
        <StatCard icon={Users} label="Visiteurs uniques" value={data.totals.visitors} hint={`sur ${data.range.days} j`} />
        <StatCard icon={Eye} label="Pages vues" value={data.totals.pageviews} hint={`sur ${data.range.days} j`} />
        <StatCard icon={Smartphone} label="Mobile" value={`${mobilePct}%`} hint={`${data.devices.mobile}/${totalDevices || 0} sessions`} />
      </div>

      {/* Line chart */}
      <div className="card-flat p-6">
        <p className="kicker">Évolution quotidienne</p>
        <div className="h-64 mt-4">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 10, right: 10, bottom: 0, left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
              <XAxis dataKey="day" stroke="#4A5568" fontSize={11} />
              <YAxis stroke="#4A5568" fontSize={11} allowDecimals={false} />
              <Tooltip
                contentStyle={{ borderRadius: 8, border: "1px solid rgba(0,0,0,0.1)", fontSize: 12 }}
                formatter={(v, name) => [v, name === "visitors" ? "Visiteurs" : "Pages vues"]}
              />
              <Line type="monotone" dataKey="visitors" stroke="#FF3B30" strokeWidth={2.5} dot={{ r: 3 }} name="visitors" />
              <Line type="monotone" dataKey="pageviews" stroke="#003366" strokeWidth={2} strokeDasharray="4 4" dot={false} name="pageviews" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <List title="Pages les plus vues" items={data.top_pages} icon={Eye} mapLabel={(k) => PAGE_LABELS[k] || k} />
        <List title="Sources de trafic" items={data.top_referrers} icon={Link2} mapLabel={(k) => REF_LABELS[k] || k} unit="visites" />
        <List title="Pays" items={(data.top_countries || []).filter((r) => r.key && r.key !== "—")} icon={MapPin} unit="visites" />
        <List title="Langues préférées" items={data.languages} icon={Languages} mapLabel={(k) => (k || "").toUpperCase()} unit="visites" />
      </div>

      {devicePie.length > 0 && (
        <div className="card-flat p-6">
          <p className="kicker flex items-center gap-1"><Monitor size={12} /> Mobile vs Desktop</p>
          <div className="h-56 mt-2 flex items-center justify-center">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={devicePie} innerRadius={50} outerRadius={80} paddingAngle={2} dataKey="value">
                  {devicePie.map((entry) => <Cell key={entry.name} fill={entry.color} />)}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex justify-center gap-6 text-sm">
            <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-[#FF3B30]" /> Mobile · {data.devices.mobile}</span>
            <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-[#003366]" /> Desktop · {data.devices.desktop}</span>
          </div>
        </div>
      )}
    </div>
  );
}
